/**
 * CaixaPdvDesktopService
 *
 * Regras de negócio para abertura, movimentação e fechamento de caixa
 * no PDV Desktop (Electron / offline-first).
 *
 * O caixa é totalmente local e é sincronizado depois com o back_nuts:
 *   POST /api/offline/caixa/sincronizar
 *
 * Regras:
 *   - Quando o controle de caixa está ativo, não permite registrar venda sem caixa aberto.
 *   - Não permite abrir caixa se já existe um caixa aberto.
 *   - Não permite fechar caixa já fechado.
 *   - Cada forma de pagamento gera uma movimentação local para permitir a
 *     conferência detalhada no fechamento às cegas.
 *   - Somente as formas que movimentam caixa são enviadas como movimento ao back;
 *     as demais continuam sendo tratadas pela sincronização da venda.
 */
import { getElectronAPI } from "@/plugins/desktop";
import CaixaLocalRepository, { CAIXA_TIPO } from "@/modules/PdvDesktop/repositories/CaixaLocalRepository";
import ApiOfflineService from "@/modules/PdvDesktop/services/ApiOfflineService";
import AppConfigRepository from "@/modules/PdvDesktop/repositories/AppConfigRepository";
import CaixaComprovantePrintService from "@/modules/PdvDesktop/services/CaixaComprovantePrintService";
import CaixaSyncService from "@/modules/PdvDesktop/services/CaixaSyncService";
import TerminalConfigService from "@/modules/PdvDesktop/services/TerminalConfigService";
import PdvDesktopAuthService from "@/modules/PdvDesktop/services/PdvDesktopAuthService";
import { resolverTpagPagamento } from "@/modules/PdvDesktop/services/NfcePagamentoFiscalMapper";

function _uuid() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function _now() {
  return new Date().toISOString();
}

function _normalizarPagamentoCaixa(pag = {}, valor = 0) {
  return {
    ...pag,
    id: pag.id ?? pag.id_forma_pagamento ?? null,
    value: valor,
    valor,
    descricao: pag.descricao ?? pag.description ?? null,
    vezes: pag.vezes ?? pag.parcelas ?? null,
    id_meio_pagamento: pag.id_meio_pagamento ?? null,
    range: Array.isArray(pag.range) && pag.range.length
      ? pag.range
      : [{ value: valor, date: _now().slice(0, 10) }],
  };
}

function _parseJson(value) {
  if (!value || typeof value !== "string") return null;

  try {
    return JSON.parse(value);
  } catch (_) {
    return null;
  }
}

function _descricaoPagamentoCaixa(pagamento) {
  const parsed = _parseJson(pagamento);
  const primeiro = Array.isArray(parsed) ? parsed[0] : parsed;

  if (primeiro && typeof primeiro === "object") {
    return primeiro.descricao || primeiro.description || primeiro.nome || primeiro.name || "Não informado";
  }

  return pagamento || "Não informado";
}

function _categoriaFormaPagamento(pagamento) {
  const parsed = _parseJson(pagamento);
  const dados = Array.isArray(parsed) ? (parsed[0] || {}) : (parsed || { descricao: pagamento });
  const tpag = resolverTpagPagamento(dados);
  if (tpag === "01") return "dinheiro";
  if (tpag === "20") return "pix";
  if (tpag === "04") return "debito";
  if (tpag === "03") return "credito";
  if (["10", "11", "12", "13"].includes(tpag)) return "beneficios";
  return "credito_loja";
}

async function _contextoTerminal() {
  const api = getElectronAPI();
  const [id_empresa_raw, uuid_terminal, terminalConfig, nomeTerminal, usuarioAtual] = await Promise.all([
    api.config.get("id_empresa"),
    api.config.get("uuid_terminal"),
    AppConfigRepository.getTerminalConfig(),
    api.config.get("terminal_nome"),
    PdvDesktopAuthService.getUsuarioAtual(),
  ]);
  return {
    id_empresa:    id_empresa_raw ? parseInt(id_empresa_raw, 10) : null,
    id_usuario:    usuarioAtual && usuarioAtual.id_usuario ? usuarioAtual.id_usuario : null,
    nome_usuario:  usuarioAtual && usuarioAtual.nome ? usuarioAtual.nome : null,
    uuid_terminal: uuid_terminal  ?? null,
    device_id:     terminalConfig?.device_id ?? null,
    id_terminal_venda: terminalConfig?.id_terminal_venda ?? null,
    nome_terminal: nomeTerminal ?? null,
  };
}

function _sincronizarCaixaEmBackground() {
  if (typeof navigator !== "undefined" && navigator.onLine === false) return;
  CaixaSyncService.sincronizarCaixasPendentes().catch((e) => {
    console.warn("[CaixaPdvDesktopService] Falha ao sincronizar caixa em background:", e?.message || e);
  });
}

/**
 * Tenta gerar e imprimir comprovante. NUNCA propaga erro.
 * Em falha de impressão: registro permanece com comprovante_impresso=0
 * e o caller pode exibir aviso ao usuário através do retorno.
 */
async function _gerarEImprimirComprovante(uuid_movimento) {
  try {
    return await CaixaComprovantePrintService.gerarEImprimir(uuid_movimento, { forcarImpressao: true });
  } catch (e) {
    return { ok: false, error: (e && e.message) || String(e) };
  }
}

const CaixaPdvDesktopService = {
  CAIXA_TIPO,

  /**
   * Retorna o registro de abertura do caixa atualmente aberto, ou null.
   */
  async obterCaixaAberto() {
    return CaixaLocalRepository.buscarCaixaAberto();
  },

  /**
   * Abre o caixa local.
   * @param {object} dados
   * @param {number} dados.valor_abertura
   * @param {string} [dados.observacao]
   */
  async abrirCaixa({ valor_abertura, observacao = "" } = {}) {
    const valor = Number(valor_abertura);
    if (!Number.isFinite(valor) || valor <= 0) {
      throw new Error("Informe um valor de abertura maior que zero.");
    }
    const aberto = await this.obterCaixaAberto();
    if (aberto) {
      throw new Error("Já existe um caixa aberto para este usuário.");
    }

    const ctx        = await _contextoTerminal();
    const uuid_caixa = _uuid();
    const uuid       = _uuid();
    const agora      = _now();

    const payload = {
      uuid,
      uuid_caixa,
      id_empresa:      ctx.id_empresa,
      id_usuario:      ctx.id_usuario,
      nome_usuario:    ctx.nome_usuario,
      uuid_terminal:   ctx.uuid_terminal,
      device_id:       ctx.device_id,
      id_terminal_venda: ctx.id_terminal_venda,
      nome_terminal:   ctx.nome_terminal,
      data_abertura:   agora,
      data_venda:      null,
      data_fechamento: null,
      valor_abertura:  valor,
      valor_movimento: valor,
      descricao:       observacao || "Abertura de caixa PDV Desktop",
      origem:          "pdv_desktop",
      status_sync:     "pendente",
      payload_json: JSON.stringify({
        uuid,
        uuid_caixa,
        uuid_terminal:   ctx.uuid_terminal,
        valor_abertura:  valor,
        observacao:      observacao || null,
        opened_at:       agora,
      }),
    };

    const reg = await CaixaLocalRepository.criarAberturaCaixa(payload);
    const impressao = await _gerarEImprimirComprovante(reg.uuid);
    _sincronizarCaixaEmBackground();
    return { ...reg, impressao };
  },

  /**
   * Registra a venda no caixa: cria uma movimentação local por forma de pagamento.
   * @param {object} nfce         - NFC-e finalizada (uuid_venda_terminal, chave, id remoto, etc.)
   * @param {Array}  pagamentos   - lista de pagamentos do PDV (paymentModeCollection)
   */
  async registrarVenda(nfce, pagamentos = []) {
    const aberto = await this.obterCaixaAberto();
    if (!aberto) {
      throw new Error("Não há caixa aberto para registrar a venda.");
    }
    if (!Array.isArray(pagamentos) || pagamentos.length === 0) {
      return { ok: true, total: 0 };
    }

    const ctx   = await _contextoTerminal();
    const agora = _now();
    const resultados = [];

    for (const pag of pagamentos) {
      const valor = Number(pag.value ?? pag.valor ?? 0);
      if (!valor) continue;

      const uuid = _uuid();
      const pagamentoCaixa = _normalizarPagamentoCaixa(pag, valor);
      const payload = {
        uuid,
        uuid_caixa:      aberto.uuid_caixa,
        id_empresa:      ctx.id_empresa,
        id_usuario:      ctx.id_usuario,
        nome_usuario:    ctx.nome_usuario,
        uuid_terminal:   ctx.uuid_terminal,
        device_id:       ctx.device_id,
        id_terminal_venda: ctx.id_terminal_venda,
        nome_terminal:   ctx.nome_terminal,
        id_nfe:          nfce && nfce.id_nfe       ? nfce.id_nfe       : null,
        data_venda:      agora,
        aberto:          1,
        pagamento:       JSON.stringify([pagamentoCaixa]),
        valor_movimento: valor,
        descricao:       `Venda PDV Desktop (${pag.descricao ?? "pagamento"})`,
        origem:          "pdv_desktop",
        status_sync:     "pendente",
        payload_json: JSON.stringify({
          uuid,
          uuid_caixa:          aberto.uuid_caixa,
          uuid_venda_terminal: nfce && nfce.uuid_venda_terminal ? nfce.uuid_venda_terminal : null,
          chave_nfce:          nfce && nfce.chave               ? nfce.chave               : null,
          protocolo:           nfce && nfce.protocolo           ? nfce.protocolo           : null,
          id_nfe:              nfce && nfce.id_nfe              ? nfce.id_nfe              : null,
          forma_pagamento: pagamentoCaixa,
        }),
      };

      const reg = await CaixaLocalRepository.registrarMovimentoVenda(payload);
      resultados.push(reg);
    }

    _sincronizarCaixaEmBackground();
    return { ok: true, total: resultados.length, movimentos: resultados };
  },

  /**
   * Registra uma sangria (saída de dinheiro do caixa).
   */
  async registrarSangria({ valor, descricao = "" } = {}) {
    const aberto = await this.obterCaixaAberto();
    if (!aberto) throw new Error("Não há caixa aberto para registrar sangria.");

    const v = Number(valor) || 0;
    if (v <= 0) throw new Error("Valor da sangria deve ser maior que zero.");
    if (!descricao || !String(descricao).trim()) {
      throw new Error("Informe o motivo/observação da sangria.");
    }

    const ctx  = await _contextoTerminal();
    const uuid = _uuid();
    const payload = {
      uuid,
      uuid_caixa:      aberto.uuid_caixa,
      id_empresa:      ctx.id_empresa,
      id_usuario:      ctx.id_usuario,
      nome_usuario:    ctx.nome_usuario,
      uuid_terminal:   ctx.uuid_terminal,
      device_id:       ctx.device_id,
      id_terminal_venda: ctx.id_terminal_venda,
      nome_terminal:   ctx.nome_terminal,
      data_venda:      _now(),
      pagamento:       "Sangria",
      valor_movimento: v,
      descricao:       descricao,
      origem:          "pdv_desktop",
      status_sync:     "pendente",
      payload_json:    JSON.stringify({ uuid, uuid_caixa: aberto.uuid_caixa, valor: v, descricao }),
    };
    const reg = await CaixaLocalRepository.registrarSangria(payload);
    const impressao = await _gerarEImprimirComprovante(reg.uuid);
    _sincronizarCaixaEmBackground();
    return { ...reg, impressao };
  },

  /**
   * Registra um suprimento (entrada de dinheiro no caixa).
   */
  async registrarSuprimento({ valor, descricao = "" } = {}) {
    const aberto = await this.obterCaixaAberto();
    if (!aberto) throw new Error("Não há caixa aberto para registrar suprimento.");

    const v = Number(valor) || 0;
    if (v <= 0) throw new Error("Valor do suprimento deve ser maior que zero.");
    if (!descricao || !String(descricao).trim()) {
      throw new Error("Informe o motivo/observação do suprimento.");
    }

    const ctx  = await _contextoTerminal();
    const uuid = _uuid();
    const payload = {
      uuid,
      uuid_caixa:      aberto.uuid_caixa,
      id_empresa:      ctx.id_empresa,
      id_usuario:      ctx.id_usuario,
      nome_usuario:    ctx.nome_usuario,
      uuid_terminal:   ctx.uuid_terminal,
      device_id:       ctx.device_id,
      id_terminal_venda: ctx.id_terminal_venda,
      nome_terminal:   ctx.nome_terminal,
      data_venda:      _now(),
      pagamento:       "Suprimento",
      valor_movimento: v,
      descricao:       descricao,
      origem:          "pdv_desktop",
      status_sync:     "pendente",
      payload_json:    JSON.stringify({ uuid, uuid_caixa: aberto.uuid_caixa, valor: v, descricao }),
    };
    const reg = await CaixaLocalRepository.registrarSuprimento(payload);
    const impressao = await _gerarEImprimirComprovante(reg.uuid);
    _sincronizarCaixaEmBackground();
    return { ...reg, impressao };
  },

  /**
   * Fecha o caixa atualmente aberto.
   * @param {object} dados
   * @param {number} dados.valor_fechamento_informado - valor contado pelo operador
   * @param {string} [dados.observacao]
   */
  async fecharCaixa({
    valor_fechamento_informado = 0,
    fechamento_pagamentos = null,
    fechamento_as_cegas = false,
    observacao = "",
  } = {}) {
    const aberto = await this.obterCaixaAberto();
    if (!aberto) throw new Error("Não há caixa aberto para fechar.");

    const ctx     = await _contextoTerminal();
    const resumo  = await this.calcularResumoCaixa(aberto.uuid_caixa);
    const uuid    = _uuid();
    const informado = Number(valor_fechamento_informado) || 0;
    const pagamentosInformados = fechamento_pagamentos && typeof fechamento_pagamentos === "object"
      ? Object.fromEntries(
        Object.entries(fechamento_pagamentos).map(([forma, valor]) => [forma, Number(valor) || 0])
      )
      : null;
    const esperadoFechamento = fechamento_as_cegas
      ? Object.values(resumo.vendas_por_categoria || {}).reduce((total, valor) => total + (Number(valor) || 0), 0)
      : resumo.valor_esperado;

    const payload = {
      uuid,
      uuid_caixa:      aberto.uuid_caixa,
      id_empresa:      ctx.id_empresa,
      id_usuario:      ctx.id_usuario,
      nome_usuario:    ctx.nome_usuario,
      uuid_terminal:   ctx.uuid_terminal,
      device_id:       ctx.device_id,
      id_terminal_venda: ctx.id_terminal_venda,
      nome_terminal:   ctx.nome_terminal,
      data_fechamento: _now(),
      pagamento:       "Fechamento",
      valor_movimento: informado,
      descricao:       observacao || "Fechamento de caixa PDV Desktop",
      origem:          "pdv_desktop",
      status_sync:     "pendente",
      payload_json: JSON.stringify({
        uuid,
        uuid_caixa:                 aberto.uuid_caixa,
        valor_fechamento_informado: informado,
        valor_esperado:             esperadoFechamento,
        diferenca:                  informado - esperadoFechamento,
        fechamento_as_cegas:       fechamento_as_cegas === true,
        fechamento_pagamentos:     pagamentosInformados,
        resumo,
        observacao,
      }),
    };

    const reg = await CaixaLocalRepository.registrarFechamento(payload);
    const impressao = await _gerarEImprimirComprovante(reg.uuid);
    _sincronizarCaixaEmBackground();
    return { ...reg, impressao };
  },

  /**
   * Calcula um resumo do caixa atual:
   *   abertura, vendas por forma, total dinheiro, sangrias, suprimentos,
   *   e valor esperado em caixa (dinheiro).
   */
  async calcularResumoCaixa(uuid_caixa) {
    const movs = await CaixaLocalRepository.buscarMovimentosPorUuidCaixa(uuid_caixa);

    let abertura     = 0;
    let totalVendas  = 0;
    let totalDinheiro = 0;
    let sangrias     = 0;
    let sangriasOperacionais = 0;
    let totalCanceladas = 0;
    let suprimentos  = 0;
    const vendasPorForma = {};
    const vendasPorCategoria = {
      dinheiro: 0,
      pix: 0,
      credito_loja: 0,
      debito: 0,
      credito: 0,
      beneficios: 0,
    };

    for (const m of movs) {
      const v = Number(m.valor_movimento) || 0;
      switch (Number(m.id_tipo)) {
        case CAIXA_TIPO.ABERTURA:
          abertura += Number(m.valor_abertura) || v;
          break;
        case CAIXA_TIPO.VENDA: {
          totalVendas += v;
          const forma = _descricaoPagamentoCaixa(m.pagamento);
          vendasPorForma[forma] = (vendasPorForma[forma] || 0) + v;
          const categoria = _categoriaFormaPagamento(m.pagamento);
          vendasPorCategoria[categoria] += v;
          if (categoria === "dinheiro") totalDinheiro += v;
          break;
        }
        case CAIXA_TIPO.SANGRIA:
          sangrias += v;
          if (_parseJson(m.payload_json)?.tipo === "cancelamento_pre_venda") {
            totalCanceladas += v;
          } else {
            sangriasOperacionais += v;
          }
          break;
        case CAIXA_TIPO.SUPRIMENTO:
          suprimentos += v;
          break;
        default:
          break;
      }
    }

    const valor_esperado = abertura + totalDinheiro + suprimentos - sangrias;
    vendasPorCategoria.dinheiro = abertura + vendasPorCategoria.dinheiro + suprimentos - sangrias;

    return {
      uuid_caixa,
      abertura,
      total_vendas:     totalVendas,
      vendas_por_forma: vendasPorForma,
      vendas_por_categoria: vendasPorCategoria,
      total_dinheiro:   totalDinheiro,
      sangrias,
      sangrias_operacionais: sangriasOperacionais,
      total_vendas_canceladas: totalCanceladas,
      suprimentos,
      valor_esperado,
      total_movimentos: movs.length,
    };
  },

  /**
   * Envia todos os registros caixa_local pendentes para o backend.
   *
   * POST /api/offline/caixa/sincronizar
   * Body: { uuid_terminal, device_id, caixas: [...] }
   */
  async sincronizarCaixaPendente() {
    return CaixaSyncService.sincronizarCaixasPendentes();
  },
};

export default CaixaPdvDesktopService;
